import { useEffect, useRef, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { isBlank, isNumericId } from "@racoon/lib";
import {
  useGetPickupLocationByIdQuery,
  useListPickupLocationsQuery,
} from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { filtersExcluding, type LocationCriteria } from "../lib/location-criteria";
import { TAB_STATUSES } from "../lib/location-list";
import type { CgRouteIndex } from "../lib/cascade-rows";

/** The filters judged off a location's container groups, all unset. */
const NO_GROUP_FILTERS = {
  customerId: "",
  containerTypeId: "",
  routeGroupId: "",
  truckId: "",
  appAccess: "",
  withoutRouteGroup: "",
} as const;

/**
 * The location a `highlightLocation` deep link points at.
 *
 * GetPickupLocationById names its status but loads no container groups, so the full
 * row — as the list loads it — is found by a list search for its name. A location with
 * no name, or one the search misses, is followed off the by-id record alone.
 *
 * A followed link stays the target after its URL param is dropped (clicking the
 * location's name drops it), so the location stays pinned until it is released.
 */
export const useDeepLinkTarget = (
  companyId: number | null | undefined,
  highlightLocationId: string | null,
) => {
  const [followedId, setFollowedId] = useState<string | null>(null);
  const urlId =
    highlightLocationId && isNumericId(highlightLocationId) ? highlightLocationId : null;
  const id = urlId ?? followedId;
  const { data: byId } = useGetPickupLocationByIdQuery(
    { id: BigInt(id ?? 0), serviceProviderId: BigInt(companyId ?? 0) },
    { skip: !id || !companyId },
  );
  const record = byId && String(byId.id) === id ? byId : undefined;
  const name = record?.name.trim() ?? "";
  // One page is enough: the row is picked out by id (the list has no id filter), and a
  // common name would otherwise bring every match with its container groups.
  const {
    data: searched,
    isFetching,
    isError: searchFailed,
  } = useListPickupLocationsQuery(
    {
      serviceProviderIds: [BigInt(companyId ?? 0)],
      statuses: record ? [record.status] : [],
      search: name,
      listFilter: { currentPage: 0, pageSize: 100 },
    },
    { skip: !record || isBlank(name) },
  );
  const full = searched?.pickupLocations.find((location) => String(location.id) === id);
  return {
    id,
    /** The id the URL names right now, if any. */
    urlId,
    record,
    /** The row with its container groups, once found. */
    full,
    /**
     * The search has settled, or was not needed: the link can be followed. A failed search
     * settles it too, and the link is followed off the by-id record alone.
     */
    resolved: !!record && (isBlank(name) || (!isFetching && (!!searched || searchFailed))),
    providerIds: record?.serviceProviderId ? [String(record.serviceProviderId)] : [],
    /** The link that has been followed and is pinned, if any. */
    followedId,
    follow: setFollowedId,
    /** A filter the user changes releases the pinned location. */
    release: () => setFollowedId(null),
  };
};

/**
 * Follows a deep link once: selects its tab, clears the filters that would hide it,
 * and pins the location to the top of the list until the user changes a filter. The
 * pinned id outlives the URL param (see `useDeepLinkTarget`), so opening the location's
 * card does not unpin, collapse or unmark it.
 */
export const useFollowDeepLink = ({
  target,
  routeIndex,
  routeIndexReady,
  criteria,
  status,
  focusLocation,
}: {
  target: ReturnType<typeof useDeepLinkTarget>;
  routeIndex: CgRouteIndex;
  routeIndexReady: boolean;
  criteria: LocationCriteria;
  status: number;
  focusLocation: (status: ConnectProto.LocationStatus, clearKeys: string[]) => void;
}) => {
  const followedRef = useRef<string | null>(null);

  // A link that arrives again, after its param was gone, is followed again. One that stays
  // in the URL is not: it would clear a filter the user has just set.
  const lastUrlIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (target.urlId && !lastUrlIdRef.current) followedRef.current = null;
    lastUrlIdRef.current = target.urlId;
  }, [target.urlId]);

  useEffect(() => {
    const { id, record, full, resolved, follow } = target;
    if (!id || !record || followedRef.current === id) return;
    // The route-group and truck filters are judged against the route index.
    if (!resolved || !routeIndexReady) return;
    followedRef.current = id;
    // No tab lists a location whose status is neither active nor archived.
    if (!TAB_STATUSES.includes(record.status)) return;
    // Without the full row its container groups are unknown: only the filters the
    // by-id record can answer are judged.
    const judged = full ? criteria : { ...criteria, ...NO_GROUP_FILTERS };
    focusLocation(record.status, filtersExcluding(full ?? record, judged, routeIndex));
    follow(id);
  }, [target, routeIndexReady, criteria, routeIndex, focusLocation]);

  const { followedId } = target;
  const pinned =
    followedId && target.full && target.id === followedId && target.full.status === status
      ? target.full
      : null;

  return {
    deepLinkedId: followedId,
    pinned,
    release: target.release,
  };
};
